import { useEffect, type RefObject } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not(:disabled)",
  "input:not(:disabled)",
  "select:not(:disabled)",
  "textarea:not(:disabled)",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Traps keyboard focus within a container element when active.
 * On open, moves focus into the container — but only while focus is still
 * outside it. On Tab, cycles forward. On Shift+Tab, cycles backward.
 *
 * This primitive exists in FIVE copies, and a behavior change belongs in every
 * one of them: here, `packages/form-ui/src/use-focus-trap.ts`, and
 * `apps/{admin,company,customer}/src/hooks/use-focus-trap.ts`. Copies rather
 * than one shared module because the consumers pull in opposite directions:
 * this one serves the app's NON-form dialog surfaces (`components/ui/drawer`,
 * `components/ui/dialog-shell`, `components/labs/reroute-modal`), which must not
 * take a dependency on the form package; the form package has to be
 * self-contained and cannot reach back into `apps/web`; and each portal is a
 * standalone app behind its own copied `DialogShell`.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const container = containerRef.current;
    if (!container) return;

    // Save the element that had focus before the trap opened
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus INTO the container — but only when it is still outside. Focus
    // already on a control in here was put there deliberately, and this runs a
    // frame late: claiming it discards that choice and sends keystrokes already
    // aimed at that control to whichever one this picks.
    const focusFirst = () => {
      const activeEl = document.activeElement;
      if (activeEl !== container && container.contains(activeEl)) return;
      const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length > 0) {
        (focusable[0] as HTMLElement).focus();
      }
    };

    // Small delay to ensure DOM is rendered
    const rafId = requestAnimationFrame(focusFirst);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;

      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("keydown", handleKeyDown);
      // Restore focus to the element that was focused before the trap opened.
      // Guard against a detached node (parent may have navigated away while
      // the dialog was open; trigger button no longer in the tree).
      // `focus()` on a detached node is a silent no-op — focus would end up
      // on `<body>`, violating WAI-ARIA APG dialog spec ("focus must move
      // to a meaningful element"). Fallback to `document.body` explicitly
      // so at least the default tab order is predictable.
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === "function" &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      } else {
        document.body.focus();
      }
    };
  }, [containerRef, active]);
}
